<template>
  <div class="appointment-report ar-hub">
    <p
      class="ar-sr-only"
      role="status"
      aria-live="polite"
    >{{ announce }}</p>

    <!-- Carregando sem trava conhecida (ex.: aberta pelo menu): neutro, para não mostrar "Acesso bloqueado" à toa. -->
    <div
      v-if="showNeutralLoading"
      class="ar-hub__loading"
      aria-busy="true"
    >
      <svg
        class="ar-spinner"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          stroke-opacity=".25"
          stroke-width="2.5"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </svg>
    </div>

    <div
      v-else
      class="ar-hub__stage"
    >
      <transition name="ar-hub-swap">
        <!-- ================================ Bloqueado / resolvendo ================================ -->
        <div
          v-if="!isReleased"
          key="blocked"
          class="ar-hub__blocked"
        >
          <section
            class="ar-hub__hero ar-anim-rise"
            :aria-labelledby="ids.title"
          >
            <div
              class="ar-hub__hero-icon"
              aria-hidden="true"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  ry="2"
                />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div class="ar-hub__hero-text">
              <h1
                :id="ids.title"
                class="ar-hub__title"
              >
                {{ APP_COPY.lockTitle }}
              </h1>
              <p class="ar-hub__lead">
                {{ APP_COPY.lockText }}
              </p>
              <p class="ar-hub__help">
                <feather-icon
                  icon="MessageCircleIcon"
                  size="16"
                  class="ar-hub__help-icon"
                  aria-hidden="true"
                />
                <span>{{ APP_COPY.helpPrefix }}</span>
                <a
                  class="ar-link"
                  :href="supportUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ APP_COPY.onlineSupport }}</a>
              </p>
            </div>
            <div
              v-if="total"
              class="ar-card ar-hub__progress-card"
            >
              <p class="ar-hub__count ar-tabular">
                <span
                  :key="resolvedCount"
                  class="ar-hub__count-value ar-anim-fade-in"
                >{{ NEW_COPY.resolvedCount(resolvedCount, total) }}</span>
                <span class="ar-hub__count-suffix">{{ NEW_COPY.resolvedSuffix }}</span>
              </p>
              <div
                role="progressbar"
                :aria-label="A11Y_COPY.pendenciesProgress"
                aria-valuemin="0"
                :aria-valuemax="total"
                :aria-valuenow="resolvedCount"
                :aria-valuetext="`${NEW_COPY.resolvedCount(resolvedCount, total)} ${NEW_COPY.resolvedSuffix}`"
                class="ar-hub__segments"
                :style="segmentsStyle"
              >
                <span
                  v-for="(filled, index) in segments"
                  :key="index"
                  class="ar-hub__seg"
                >
                  <span
                    class="ar-hub__seg-fill"
                    :style="{ transform: `scaleX(${filled})` }"
                  />
                </span>
              </div>
            </div>
          </section>

          <!-- Falha ao carregar a lista -->
          <section
            v-if="status === STATUS.ERROR"
            class="ar-card ar-hub__error ar-anim-rise"
            style="--ar-delay: 40ms"
          >
            <div
              class="ar-callout ar-callout--danger"
              role="alert"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="20"
                aria-hidden="true"
              />
              <p class="ar-hub__error-text">
                {{ NEW_COPY.pendenciesLoadError }}
              </p>
            </div>
            <button
              type="button"
              class="ar-btn ar-btn--secondary ar-btn--sm"
              @click="load"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="16"
                aria-hidden="true"
              />
              {{ NEW_COPY.retry }}
            </button>
          </section>

          <div
            v-else
            class="ar-hub__split"
          >
            <!-- ---- Lista (mais antiga primeiro) ---- -->
            <section
              class="ar-card ar-hub__list ar-anim-rise"
              style="--ar-delay: 40ms"
              :aria-labelledby="ids.listTitle"
              :aria-busy="isLoading ? 'true' : 'false'"
            >
              <h2
                :id="ids.listTitle"
                class="ar-hub__list-title"
              >
                {{ NEW_COPY.pendenciesTitle }}
              </h2>

              <ul
                v-if="isLoading"
                class="ar-hub__items"
                aria-hidden="true"
              >
                <li
                  v-for="index in 3"
                  :key="index"
                  class="ar-hub__skeleton"
                />
              </ul>

              <ul
                v-else
                role="list"
                class="ar-hub__items"
              >
                <li
                  v-for="(item, index) in items"
                  :key="item.id"
                  class="ar-hub__item ar-anim-rise"
                  :style="{ '--ar-delay': `${80 + Math.min(index, 8) * 40}ms` }"
                >
                  <button
                    :id="cardId(item.id)"
                    type="button"
                    class="ar-hub__card"
                    :class="{
                      'ar-hub__card--selected': item.id === selectedId,
                      'ar-hub__card--resolved': isResolved(item.id),
                    }"
                    :aria-current="item.id === selectedId ? 'true' : null"
                    @click="select(item.id)"
                    @keydown="onCardKeydown($event, index)"
                  >
                    <span
                      v-if="flash[item.id]"
                      class="ar-hub__card-flash"
                      aria-hidden="true"
                    />
                    <span
                      class="ar-avatar ar-avatar--md"
                      aria-hidden="true"
                    >{{ item.initials }}</span>
                    <span class="ar-hub__card-main">
                      <span class="ar-hub__card-line">
                        <span class="ar-hub__card-name">{{ item.customerName }}</span>
                        <span
                          v-if="item.typeLabel"
                          class="ar-chip ar-hub__card-chip"
                          :class="`ar-chip--${item.tone}`"
                        >{{ item.typeLabel }}</span>
                        <span
                          v-if="item.hasDraft && !isResolved(item.id)"
                          class="ar-chip ar-chip--outline ar-hub__card-chip ar-hub__card-chip--draft"
                        ><span class="ar-hub__card-chip-text">{{ NEW_COPY.draftStarted }}</span></span>
                      </span>
                      <span class="ar-hub__card-line">
                        <span class="ar-hub__card-date ar-tabular">{{ item.listWhen }}</span>
                        <span
                          v-if="!isResolved(item.id) && item.daysOverdue > 0"
                          class="ar-chip"
                          :class="item.daysOverdue >= LATE_DAYS ? 'ar-chip--danger' : 'ar-chip--warning'"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                            />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {{ NEW_COPY.daysOverdue(item.daysOverdue) }}
                        </span>
                      </span>
                    </span>
                    <span class="ar-hub__card-status">
                      <span
                        v-if="isResolved(item.id)"
                        class="ar-chip ar-chip--success ar-anim-chip-pop"
                      >
                        <feather-icon
                          icon="CheckIcon"
                          size="14"
                          aria-hidden="true"
                        />
                        {{ NEW_COPY.resolved }}
                      </span>
                      <feather-icon
                        v-else
                        icon="ChevronRightIcon"
                        size="16"
                        class="ar-hub__card-chevron"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </li>
              </ul>

              <p class="ar-hub__list-caption">
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                  aria-hidden="true"
                />
                <span>{{ NEW_COPY.lockProgressCaption }}</span>
              </p>
            </section>

            <!-- ---- Painel: o report da pendência selecionada, sem sair da página ---- -->
            <section
              ref="pane"
              class="ar-card ar-hub__pane ar-anim-rise"
              style="--ar-delay: 80ms"
              :aria-labelledby="selected ? ids.paneTitle : ids.listTitle"
            >
              <!-- Carregando — ou lista vazia com a trava ainda no store (reconsultando, ver checkPendingUnlock). -->
              <div
                v-if="isLoading || !selected"
                class="ar-hub__pane-loading"
                aria-busy="true"
              >
                <svg
                  class="ar-spinner"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="currentColor"
                    stroke-opacity=".25"
                    stroke-width="2.5"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>

              <div
                v-else
                :key="selected.id"
                class="ar-hub__pane-inner ar-anim-pane-in"
              >
                <header class="ar-hub__pane-head">
                  <span
                    class="ar-avatar ar-avatar--lg"
                    aria-hidden="true"
                  >{{ selected.initials }}</span>
                  <div class="ar-hub__pane-heading">
                    <div class="ar-hub__pane-title-row">
                      <h2
                        :id="ids.paneTitle"
                        ref="paneTitle"
                        tabindex="-1"
                        class="ar-hub__pane-title ar-focus-target"
                      >
                        {{ selected.customerName }}
                      </h2>
                      <span
                        v-if="selected.typeLabel"
                        class="ar-chip"
                        :class="`ar-chip--${selected.tone}`"
                      >{{ selected.typeLabel }}</span>
                    </div>
                    <p class="ar-hub__pane-date ar-tabular">
                      <feather-icon
                        icon="CalendarIcon"
                        size="14"
                        class="ar-hub__pane-date-icon"
                        aria-hidden="true"
                      />
                      {{ selected.date }} | {{ selected.timeRange }}
                    </p>
                  </div>
                  <span class="ar-hub__pane-position ar-tabular">
                    {{ NEW_COPY.pendencyPosition(selectedIndex + 1, total) }}
                  </span>
                </header>

                <!-- Resolvida: confirmação compacta + "Próxima pendência" (nunca avança sozinho) -->
                <div
                  v-if="selectedResolution"
                  class="ar-hub__pane-body"
                >
                  <report-success
                    compact
                    heading-tag="h3"
                    :title="NEW_COPY.reportSaved"
                    :caption="savedCaption(selected)"
                    :summary="selectedSummary"
                    :report="selectedResolution.report"
                    :notice="selectedNotice"
                    :autofocus="false"
                  >
                    <template #next>
                      <!-- O report foi salvo, mas o reagendamento falhou: tenta só o reagendamento de novo -->
                      <div
                        v-if="rescheduleFailed(selected.id)"
                        class="ar-callout ar-callout--warning ar-hub__reschedule-error"
                        role="alert"
                      >
                        <feather-icon
                          icon="AlertCircleIcon"
                          size="16"
                          aria-hidden="true"
                        />
                        <span class="ar-hub__reschedule-error-text">{{ selectedResolution.reschedule.message }}</span>
                        <button
                          type="button"
                          class="ar-btn ar-btn--secondary ar-btn--sm"
                          :aria-busy="selectedResolution.reschedule.status === 'saving' ? 'true' : 'false'"
                          @click="retryReschedule(selected.id)"
                        >
                          {{ NEW_COPY.retry }}
                        </button>
                      </div>
                      <p
                        v-if="remainingCount > 0"
                        class="ar-hub__remaining"
                      >
                        <feather-icon
                          icon="LockIcon"
                          size="16"
                          class="ar-hub__remaining-icon"
                          aria-hidden="true"
                        />
                        <span>{{ NEW_COPY.remainingPendencies(remainingCount) }}</span>
                      </p>
                      <button
                        v-if="nextPendingId"
                        :id="ids.next"
                        ref="nextButton"
                        type="button"
                        class="ar-btn ar-btn--primary ar-hub__next"
                        @click="goNext"
                      >
                        {{ NEW_COPY.nextPendency }}
                        <feather-icon
                          icon="ArrowRightIcon"
                          size="16"
                          aria-hidden="true"
                        />
                      </button>
                    </template>
                  </report-success>
                </div>

                <!-- Momento X: as perguntas do CX, inline -->
                <pendency-moment-x-form
                  v-else-if="selected.isMomentX"
                  :appointment-id="selected.id"
                  @mx-report-finalized="onMomentXFinalized(selected.id, $event)"
                  @mx-report-already-reported="onAlreadyReported(selected.id, $event)"
                  @mx-report-pending-rejected="onPendingRejected(selected.id)"
                  @draft-saved="onDraftSaved(selected.id)"
                />

                <!-- Entrevista / Negociação / Parceria: as perguntas de sempre, inline (mesmo layout compacto) -->
                <standard-report-form
                  v-else
                  compact
                  :appointment-id="selected.id"
                  @reported="onStandardFinalized(selected.id, $event)"
                  @already-reported="onAlreadyReported(selected.id, $event)"
                  @pending-rejected="onPendingRejected(selected.id)"
                  @draft-saved="onDraftSaved(selected.id)"
                />
              </div>
            </section>
          </div>
        </div>

        <!-- ================================ Liberado ================================ -->
        <section
          v-else
          key="released"
          class="ar-hub__released"
          :aria-labelledby="ids.releasedTitle"
        >
          <div class="ar-hub__released-inner">
            <div
              class="ar-hub__released-icon"
              aria-hidden="true"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="ar-hub__released-lock"
              >
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  ry="2"
                />
                <path
                  class="ar-anim-shackle"
                  d="M7 11V7a5 5 0 0 1 10 0v4"
                />
              </svg>
            </div>
            <h1
              :id="ids.releasedTitle"
              class="ar-hub__title ar-hub__released-title ar-anim-rise"
              style="--ar-delay: 300ms"
            >
              {{ NEW_COPY.unlockedTitle }}
            </h1>
            <p
              class="ar-hub__lead ar-hub__released-lead ar-anim-rise"
              style="--ar-delay: 300ms"
            >
              {{ NEW_COPY.unlockedText }}
            </p>

            <div
              v-if="total"
              class="ar-card ar-hub__released-card"
            >
              <div class="ar-hub__released-head">
                <p class="ar-hub__count ar-hub__count--sm ar-tabular">
                  <span class="ar-hub__count-value">{{ NEW_COPY.resolvedCount(resolvedCount, total) }}</span>
                  <span class="ar-hub__count-suffix">{{ NEW_COPY.resolvedSuffix }}</span>
                </p>
                <div
                  class="ar-hub__segments ar-hub__segments--sm"
                  :style="segmentsStyle"
                  aria-hidden="true"
                >
                  <span
                    v-for="(filled, index) in segments"
                    :key="index"
                    class="ar-hub__seg"
                  >
                    <span
                      class="ar-hub__seg-fill"
                      :style="{ transform: `scaleX(${filled})` }"
                    />
                  </span>
                </div>
              </div>
              <ul
                role="list"
                class="ar-hub__released-list"
              >
                <template v-for="(item, index) in items">
                  <li
                    :key="item.id"
                    class="ar-hub__released-row ar-anim-rise"
                    :style="{ '--ar-delay': `${320 + Math.min(index, 8) * 40}ms` }"
                  >
                    <span
                      class="ar-avatar"
                      aria-hidden="true"
                    >{{ item.initials }}</span>
                    <span class="ar-hub__released-name">{{ item.customerName }}</span>
                    <span
                      v-if="item.typeLabel"
                      class="ar-chip"
                      :class="`ar-chip--${item.tone}`"
                    >{{ item.typeLabel }}</span>
                    <span class="ar-hub__released-end">
                      <!-- MX com negócio fechado: o cadastro do contrato, adiado enquanto havia pendência, fica aqui. -->
                      <router-link
                        v-if="canRegisterContract(item.id)"
                        class="ar-link"
                        :to="contractRoute(item.id)"
                      >{{ NEW_COPY.registerContract }}</router-link>
                      <span class="ar-chip ar-chip--success">
                        <feather-icon
                          icon="CheckIcon"
                          size="14"
                          aria-hidden="true"
                        />
                        {{ NEW_COPY.resolved }}
                      </span>
                    </span>
                  </li>
                  <!-- Último report salvo, mas o reagendamento falhou: o "Tentar novamente" segue aqui, sob o cliente -->
                  <li
                    v-if="rescheduleFailed(item.id)"
                    :key="`reschedule-${item.id}`"
                    class="ar-hub__released-reschedule"
                  >
                    <div
                      class="ar-callout ar-callout--warning ar-hub__reschedule-error"
                      role="alert"
                    >
                      <feather-icon
                        icon="AlertCircleIcon"
                        size="16"
                        aria-hidden="true"
                      />
                      <span class="ar-hub__reschedule-error-text">{{ resolutions[item.id].reschedule.message }}</span>
                      <button
                        type="button"
                        class="ar-btn ar-btn--secondary ar-btn--sm"
                        :aria-busy="resolutions[item.id].reschedule.status === 'saving' ? 'true' : 'false'"
                        @click="retryReschedule(item.id)"
                      >
                        {{ NEW_COPY.retry }}
                      </button>
                    </div>
                  </li>
                </template>
              </ul>
            </div>

            <router-link
              ref="continueLink"
              class="ar-btn ar-btn--primary ar-hub__continue"
              :to="continueTarget"
            >
              {{ APP_COPY.continue }}
            </router-link>
            <p class="ar-hub__help ar-hub__released-help">
              <feather-icon
                icon="MessageCircleIcon"
                size="16"
                class="ar-hub__help-icon"
                aria-hidden="true"
              />
              <span>{{ APP_COPY.helpPrefix }}</span>
              <a
                class="ar-link"
                :href="supportUrl"
                target="_blank"
                rel="noopener noreferrer"
              >{{ APP_COPY.onlineSupport }}</a>
            </p>
          </div>
        </section>
      </transition>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountTypes from '@/modules/account/store/types'
import * as appointmentTypes from '@/modules/appointment/store/types'
import { isReportPendingError } from '@/helpers/error_messages'
import {
  A11Y_COPY,
  APP_COPY,
  NEW_COPY,
  REPORT_HUB_RETURN_QUERY,
  REPORT_HUB_ROUTE,
  appointmentTypeLabel,
  isSafeReturnPath,
} from '@/constants/appointment_report_v2'
import {
  answersFromStandardReport,
  standardReportContext,
  standardReportSummaryLines,
} from '@/constants/appointment_report_standard'
import { X_MOMENT } from '@/constants/appointment_types'
// Ordem dos imports = ordem do CSS no chunk comum com a página do Report: ReportSuccess, depois o StandardReportForm
// (que traz ReportActionBar, ReportAppointmentCard, ReportDateLockBanner, ReportReasonSelect e ReportYesNo nessa
// ordem, a mesma do `components/index.js`) e só então o PendencyMomentXForm. Com o MX antes, o ReportAppointmentCard
// (que ele não usa) ficava depois dos outros, e o mini-css-extract acusava "Conflicting order" no build.
import ReportSuccess from '../components/ReportSuccess.vue'
import StandardReportForm from '../components/StandardReportForm.vue'
import PendencyMomentXForm from '../components/pendencies/PendencyMomentXForm.vue'
import { rescheduleErrorMessage } from '../mixins/standardReportForm'
import {
  appointmentEndForDisplay,
  eventTypeTone,
  formatDateTime,
  formatShortDate,
  formatTime,
  initialsOf,
  nextReportUid,
  prefersReducedMotion,
  toTimestamp,
} from '../helpers/report_ui'

const STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
}

// "N dias de atraso" fica vermelho a partir de 3 dias (board v4-pendencias); antes, âmbar.
const LATE_DAYS = 3
// Fundo verde do card que acabou de ser resolvido (600ms, board).
const FLASH_MS = 700
// Até 10 pendências o progresso é segmentado (1 segmento por pendência); acima disso, uma barra contínua.
const MAX_SEGMENTS = 10
// Abaixo disso a lista e o painel ficam um sobre o outro (o painel entra na tela ao escolher uma pendência).
const STACKED_QUERY = '(max-width: 991.98px)'
// Voltou para a aba: reconsulta a lista (pendência resolvida em outra aba/dispositivo), no máximo 1× a cada 15 s.
const VISIBILITY_REFRESH_MIN_MS = 15000
// Tudo resolvido na tela, mas a trava continua no store: reconsulta até 3× (2 s, 4 s, 6 s).
const LOCK_RECHECK_BASE_MS = 2000
const LOCK_RECHECK_MAX = 3
const FALLBACK_ROUTE = { name: 'dashboard' }
// Destinos que não fazem sentido para o "Continuar" (a própria central, erro, login).
const NON_RETURN_ROUTES = [REPORT_HUB_ROUTE, 'error-404', 'misc-not-authorized', 'misc-error']

/** Linha de GET /api/users/report_pendencies → item da lista (ou null se vier quebrada). */
const toPendency = row => {
  const id = Number(row && row.id)
  if (!Number.isInteger(id) || id <= 0) return null
  const eventType = Number(row.event_type)
  const start = typeof row.appointment_start === 'string' ? row.appointment_start : ''
  // O backend grava o término 1 minuto antes (13:59) — exibe o horário marcado (14:00).
  const end = typeof row.appointment_end === 'string' ? appointmentEndForDisplay(row.appointment_end) : ''
  const customerName = String(row.customer_name || '').trim()
  const startTime = formatTime(start)
  const endTime = formatTime(end)

  return {
    id,
    eventType,
    // O status ajuda a montar o contexto do resumo dos tipos padrão quando só o report existe (ver `selectedSummary`).
    statusId: Number(row.status_id) || 0,
    isMomentX: eventType === X_MOMENT,
    typeLabel: appointmentTypeLabel(eventType),
    tone: eventTypeTone(eventType),
    customerName,
    initials: initialsOf(customerName),
    start,
    end,
    startTs: toTimestamp(start),
    listWhen: formatDateTime(start, ' | '),
    date: formatShortDate(start),
    timeRange: endTime ? `${startTime} – ${endTime}` : startTime,
    daysOverdue: Math.max(0, Math.floor(Number(row.days_overdue) || 0)),
    hasDraft: row.has_draft === true || row.has_draft === 1 || row.has_draft === '1',
  }
}

// Mais antiga primeiro (mesma ordem da API); empate pelo id.
const byStart = (a, b) => {
  const aTs = a.startTs === null ? Infinity : a.startTs
  const bTs = b.startTs === null ? Infinity : b.startTs
  if (aTs !== bTs) return aTs - bTs
  return a.id - b.id
}

/**
 * Central de pendências de report (`/pendencias`, board v4-pendencias): onde o PN travado resolve TODOS os
 * reports pendentes, um depois do outro, sem sair da página. Quando o último é resolvido, o acesso é liberado.
 *
 * - Lista (mais antiga primeiro) com dias de atraso, "Rascunho iniciado" e "Resolvida"; o painel ao lado mostra o
 *   report da pendência escolhida, inline e no mesmo layout compacto: Momento X → as perguntas do CX
 *   (`PendencyMomentXForm`); Entrevista, Negociação e Parceria → as perguntas de sempre (`StandardReportForm
 *   compact`), com as regras de hoje desses tipos (reagendamento, especialista…). Os dois finalizam no mesmo
 *   `POST …/report/finalize` (rascunho, idempotência, horário e dono validados no servidor).
 * - Depois de cada report: a action de finalizar já libera o compromisso na trava e dispara o `REFRESH_REPORT_LOCK`
 *   forçado; aqui a lista é recarregada (`GET_REPORT_PENDENCIES`, que também grava a trava); o card anima para
 *   "Resolvida", o progresso avança e o foco vai para "Próxima pendência". Nunca avança sozinho.
 * - Negócio fechado (Momento X, ou Parceria — a pergunta "Deseja realizar o report dos contratos…?" da sidebar): com
 *   outras pendências o cadastro fica para depois (aviso na confirmação) e aparece como "Cadastrar contrato" na
 *   liberação — o `contract-save` fica atrás da trava.
 * - Lista vazia (e sem trava) → "Acesso liberado" + "Continuar" → a tela que o PN pediu (`?voltar=`, gravado pelo
 *   guard de rotas / 423) ou o dashboard.
 *
 * A trava é do servidor: esta página só espelha `GET /api/users/report_pendencies`. Um item só vira "Resolvida"
 * quando o report foi gravado (resposta do finalize) ou quando o servidor deixa de listá-lo.
 */
const HUB_BODY_CLASS = 'ar-hub-visible'

export default {
  name: 'ReportPendenciesPage',
  components: {
    PendencyMomentXForm,
    ReportSuccess,
    StandardReportForm,
  },
  data() {
    const uid = nextReportUid('ar-hub')
    return {
      A11Y_COPY,
      APP_COPY,
      NEW_COPY,
      STATUS,
      LATE_DAYS,
      ids: {
        title: `${uid}-title`,
        listTitle: `${uid}-list-title`,
        paneTitle: `${uid}-pane-title`,
        next: `${uid}-next`,
        releasedTitle: `${uid}-released-title`,
        card: `${uid}-card`,
      },
      status: STATUS.LOADING,
      items: [],
      selectedId: null,
      // id → { at, report, closedDeal, confirmed, answers, context } — resolvida nesta sessão da página (ou sumiu da
      // lista do servidor). `confirmed` = o servidor devolveu o report (finalize / report existente). `answers` e
      // `context` (tipos padrão) = o que o formulário respondeu, para o resumo com as perguntas de sempre.
      resolutions: {},
      flash: {},
      announce: '',
      // O PN resolveu alguma pendência aqui: ao liberar, o foco vai para "Continuar".
      resolvedHere: false,
    }
  },
  computed: {
    isReportLocked() {
      return this.$store.getters[accountTypes.IS_REPORT_LOCKED]
    },
    // Especialistas (`GET interviews/proposal/consultants`, carregada pelo StandardReportForm): o nome do
    // especialista avaliado no resumo dos tipos padrão.
    specialistNames() {
      const list = this.$store.getters[appointmentTypes.CONSULTANTS_SPECIALIST]
      const rows = Array.isArray(list) ? list : []
      const names = {}
      rows.forEach(item => {
        if (item && item.id) names[String(item.id)] = String(item.name || '')
      })
      return names
    },
    isLoading() {
      return this.status === STATUS.LOADING
    },
    showNeutralLoading() {
      return this.isLoading && !this.isReportLocked
    },
    total() {
      return this.items.length
    },
    resolvedCount() {
      return this.items.filter(item => this.isResolved(item.id)).length
    },
    remainingCount() {
      return this.total - this.resolvedCount
    },
    // Liberado = a lista do servidor zerou E a trava do store saiu (o finalize/refresh atualizam as duas).
    isReleased() {
      return this.status === STATUS.READY && this.remainingCount === 0 && !this.isReportLocked
    },
    // Nada mais pendente na tela, mas a trava ainda consta no store (ver `checkPendingUnlock`).
    awaitingUnlock() {
      return this.status === STATUS.READY && this.remainingCount === 0 && this.isReportLocked
    },
    selected() {
      return this.items.find(item => item.id === this.selectedId) || null
    },
    selectedIndex() {
      return this.items.findIndex(item => item.id === this.selectedId)
    },
    selectedResolution() {
      return this.selected ? this.resolutions[this.selected.id] || null : null
    },
    /**
     * "Resumo do report" da confirmação. Momento X: o ReportSuccess monta com as perguntas do CX a partir do `report`
     * (`null` = use o report; `[]` = sem resumo). Tipos padrão: as perguntas de sempre — com as respostas do
     * formulário quando o report foi feito aqui (trazem os destaques do especialista), senão as do report gravado
     * (já reportado em outra aba), no contexto do tipo do compromisso.
     */
    selectedSummary() {
      const item = this.selected
      const resolution = this.selectedResolution
      if (!item || !resolution) return []
      if (item.isMomentX) return resolution.report ? null : []
      const answers = resolution.answers || (resolution.report ? answersFromStandardReport(resolution.report) : null)
      if (!answers) return []
      const context = resolution.context
        || standardReportContext({ event_type: item.eventType, status_id: item.statusId })

      return standardReportSummaryLines(answers, context, {
        specialistName: id => this.specialistNames[String(id)] || '',
      })
    },
    // Negócio fechado (MX ou Parceria) e ainda com pendências: o cadastro do contrato fica para depois (aparece ao
    // liberar).
    selectedNotice() {
      const resolution = this.selectedResolution
      return resolution && resolution.closedDeal && this.remainingCount > 0 ? NEW_COPY.contractAfterPendencies : ''
    },
    // Próxima não resolvida depois da selecionada (dá a volta na lista).
    nextPendingId() {
      const count = this.items.length
      const start = Math.max(0, this.selectedIndex)
      for (let step = 1; step <= count; step += 1) {
        const item = this.items[(start + step) % count]
        if (item.id !== this.selectedId && !this.isResolved(item.id)) return item.id
      }
      return null
    },
    segments() {
      if (this.total > MAX_SEGMENTS) return [this.total ? this.resolvedCount / this.total : 0]
      return this.items.map((item, index) => (index < this.resolvedCount ? 1 : 0))
    },
    segmentsStyle() {
      return { gridTemplateColumns: `repeat(${this.segments.length || 1}, minmax(0, 1fr))` }
    },
    continueTarget() {
      const raw = this.$route.query[REPORT_HUB_RETURN_QUERY]
      const value = Array.isArray(raw) ? raw[0] : raw
      if (!isSafeReturnPath(value)) return FALLBACK_ROUTE
      const { route } = this.$router.resolve(value)
      if (!route || !route.name || !route.matched.length) return FALLBACK_ROUTE
      if (NON_RETURN_ROUTES.includes(route.name) || route.name.indexOf('account-') === 0) return FALLBACK_ROUTE

      return value
    },
    supportUrl() {
      return `https://wa.me/${process.env.VUE_APP_CHATWOOT_NUMBER || ''}`
    },
  },
  watch: {
    isReleased(released, wasReleased) {
      if (!released || wasReleased) return
      this.clearLockRecheck()
      this.announce = NEW_COPY.unlockedTitle
      if (this.resolvedHere) this.$nextTick(() => this.focusContinue())
    },
  },
  created() {
    this.$_destroyed = false
    this.$_refreshRequest = null
    this.$_refreshQueued = null
    this.$_lastRefreshAt = 0
    this.$_flashTimers = {}
    this.$_lockRecheckTimer = null
    this.$_lockRechecks = 0
    // id → true: saiu das pendências sem report (423 da trava no formulário) — some da lista, não vira "Resolvida".
    this.$_withdrawnIds = {}
    this.load()
  },
  mounted() {
    // Abaixo de 1200px os botões fixos do App.vue ("Atendimento online" / "Feedback de erro") cobririam a lista
    // de pendências — somem enquanto a central está aberta (ela já oferece "Atendimento online" no topo).
    document.body.classList.add(HUB_BODY_CLASS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    this.$_destroyed = true
    document.body.classList.remove(HUB_BODY_CLASS)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    Object.keys(this.$_flashTimers).forEach(id => clearTimeout(this.$_flashTimers[id]))
    this.clearLockRecheck()
  },
  methods: {
    // ---------------------------------------------------------------------------------------------------------
    // Lista
    // ---------------------------------------------------------------------------------------------------------
    load() {
      this.status = STATUS.LOADING
      const requestedAt = Date.now()
      this.$_lastRefreshAt = requestedAt

      return this.$store.dispatch(appointmentTypes.GET_REPORT_PENDENCIES)
        .then(resp => {
          if (this.$_destroyed) return
          this.applyPendencies(resp && resp.data, requestedAt)
        })
        .catch(() => {
          if (this.$_destroyed) return
          this.status = STATUS.ERROR
        })
    },

    /**
     * Recarrega a lista (e a trava) em segundo plano. Uma por vez; pedidos durante uma em andamento viram UMA nova
     * consulta no fim. Falhou → mantém a tela como está (o servidor continua aplicando a trava).
     * `activeId` = pendência em que o PN acabou de agir (se o servidor não a listar mais, anima e move o foco).
     */
    refresh(options) {
      const opts = options || {}
      if (this.$_refreshRequest) {
        this.$_refreshQueued = { ...(this.$_refreshQueued || {}), ...opts }
        return this.$_refreshRequest
      }
      const requestedAt = Date.now()
      this.$_lastRefreshAt = requestedAt
      const request = this.$store.dispatch(appointmentTypes.GET_REPORT_PENDENCIES)
        .then(resp => {
          if (this.$_destroyed) return
          this.applyPendencies(resp && resp.data, requestedAt, opts.activeId)
          // O item aberto saiu da lista: o foco (que estava no "Finalizar" dele) vai para a pendência que entrou.
          if (opts.withdrawnId && this.selectedId !== opts.withdrawnId) this.$nextTick(() => this.focusPaneTitle())
        })
        .catch(() => {})
        .finally(() => {
          this.$_refreshRequest = null
          const queued = this.$_refreshQueued
          this.$_refreshQueued = null
          if (queued && !this.$_destroyed) this.refresh(queued)
        })
      this.$_refreshRequest = request

      return request
    },

    /**
     * Mescla a resposta do servidor com a tela: itens resolvidos continuam na lista ("Resolvida"); o que o servidor
     * deixou de listar vira resolvido; o que ele ainda lista volta a pendente (ex.: o report legado falhou) — só se a
     * consulta foi feita DEPOIS da resolução local; pendências novas entram na ordem.
     * Um report CONFIRMADO pelo servidor (resposta do finalize / report existente) nunca volta a pendente: remontar
     * o formulário só acharia o mesmo report de novo, num ciclo de requisições. A trava é que decide a liberação.
     */
    applyPendencies(body, requestedAt, activeId) {
      const rows = (body && Array.isArray(body.data) ? body.data : []).map(toPendency).filter(Boolean)
      const serverIds = {}
      const merged = {}
      this.items.forEach(item => {
        merged[item.id] = item
      })
      rows.forEach(row => {
        serverIds[row.id] = true
        delete this.$_withdrawnIds[row.id]
        const current = merged[row.id]
        merged[row.id] = current ? { ...current, ...row, hasDraft: row.hasDraft || current.hasDraft } : row
        const resolution = this.resolutions[row.id]
        if (resolution && !resolution.confirmed && resolution.at <= requestedAt) {
          this.$delete(this.resolutions, row.id)
        }
      })
      this.items = Object.keys(merged).map(id => merged[id])
        .filter(item => serverIds[item.id] || !this.$_withdrawnIds[item.id])
        .sort(byStart)
      this.items.forEach(item => {
        if (!serverIds[item.id] && !this.resolutions[item.id]) {
          this.markResolved(item.id, {}, { quiet: item.id !== activeId })
        }
      })
      if (!this.selected) {
        const firstPending = this.items.find(item => !this.isResolved(item.id))
        this.selectedId = (firstPending || this.items[0] || {}).id || null
      }
      this.status = STATUS.READY
      this.checkPendingUnlock()
    },

    /**
     * Nada mais pendente na tela, mas a trava continua no store — ex.: um 423 de outra requisição chegou durante a
     * consulta e a resposta dela foi descartada como antiga. Reconsulta poucas vezes, com espera crescente; depois
     * disso, só a volta para a aba (ou uma nova ação) consulta de novo.
     */
    checkPendingUnlock() {
      this.clearLockRecheck()
      if (!this.awaitingUnlock) {
        this.$_lockRechecks = 0
        return
      }
      if (this.$_destroyed || this.$_lockRechecks >= LOCK_RECHECK_MAX) return
      this.$_lockRechecks += 1
      this.$_lockRecheckTimer = setTimeout(() => {
        this.$_lockRecheckTimer = null
        if (!this.$_destroyed) this.refresh()
      }, LOCK_RECHECK_BASE_MS * this.$_lockRechecks)
    },

    clearLockRecheck() {
      clearTimeout(this.$_lockRecheckTimer)
      this.$_lockRecheckTimer = null
    },

    // Voltou para a aba: a pendência pode ter sido resolvida em outro lugar (ou surgido outra). Em silêncio: o
    // que o servidor deixou de listar vira "Resolvida" sem mover o foco.
    onVisibilityChange() {
      if (document.visibilityState !== 'visible' || this.status !== STATUS.READY) return
      if (Date.now() - this.$_lastRefreshAt < VISIBILITY_REFRESH_MIN_MS) return
      this.refresh()
    },

    isResolved(id) {
      return !!this.resolutions[id]
    },

    itemById(id) {
      return this.items.find(item => item.id === id) || null
    },

    /**
     * Marca como resolvida (card verde + "Resolvida", progresso avança). Fora do modo `quiet` (resolvida pelo PN
     * nesta tela) anuncia "Report salvo: <cliente>" e leva o foco para "Próxima pendência".
     */
    markResolved(id, info, options) {
      const existing = this.resolutions[id]
      this.$set(this.resolutions, id, {
        at: Date.now(),
        report: null,
        closedDeal: false,
        confirmed: false,
        answers: null,
        context: null,
        // `{ status, message, payload }` do reagendamento que falhou depois do report (ver `retryReschedule`).
        reschedule: null,
        ...existing,
        ...info,
      })
      if (existing) return
      this.flashCard(id)
      if (options && options.quiet) return

      this.resolvedHere = true
      const item = this.itemById(id)
      this.announce = NEW_COPY.reportSavedFor(item ? item.customerName : '')
      this.$nextTick(() => {
        if (!this.isReleased && this.selectedId === id) this.focusNext()
      })
    },

    flashCard(id) {
      this.$set(this.flash, id, true)
      clearTimeout(this.$_flashTimers[id])
      this.$_flashTimers[id] = setTimeout(() => {
        delete this.$_flashTimers[id]
        this.$delete(this.flash, id)
      }, FLASH_MS)
    },

    // ---------------------------------------------------------------------------------------------------------
    // Seleção e teclado
    // ---------------------------------------------------------------------------------------------------------
    cardId(id) {
      return `${this.ids.card}-${id}`
    },

    select(id) {
      if (id === this.selectedId) return
      this.selectedId = id
      // Lista e painel empilhados (tela estreita): traz o painel para a tela.
      if (this.isStacked()) this.$nextTick(() => this.scrollPaneIntoView())
    },

    onCardKeydown(event, index) {
      const count = this.items.length
      let target = -1
      if (event.key === 'ArrowDown') target = (index + 1) % count
      else if (event.key === 'ArrowUp') target = (index - 1 + count) % count
      else if (event.key === 'Home') target = 0
      else if (event.key === 'End') target = count - 1
      if (target < 0) return
      event.preventDefault()
      const element = document.getElementById(this.cardId(this.items[target].id))
      if (element) element.focus()
    },

    goNext() {
      const nextId = this.nextPendingId
      if (!nextId) return
      this.selectedId = nextId
      this.$nextTick(() => {
        if (this.isStacked()) this.scrollPaneIntoView()
        this.focusPaneTitle()
      })
    },

    isStacked() {
      try {
        return !!(window.matchMedia && window.matchMedia(STACKED_QUERY).matches)
      } catch (error) {
        return false
      }
    },

    scrollPaneIntoView() {
      const { pane } = this.$refs
      if (pane && typeof pane.scrollIntoView === 'function') {
        pane.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
      }
    },

    focusPaneTitle() {
      const { paneTitle } = this.$refs
      if (paneTitle) paneTitle.focus({ preventScroll: this.isStacked() })
    },

    focusNext() {
      const { nextButton } = this.$refs
      if (nextButton) nextButton.focus()
    },

    focusContinue() {
      const link = this.$refs.continueLink
      const element = link && (link.$el || link)
      if (element && typeof element.focus === 'function') element.focus()
    },

    // ---------------------------------------------------------------------------------------------------------
    // Formulários inline (Momento X e tipos padrão)
    // ---------------------------------------------------------------------------------------------------------
    onMomentXFinalized(id, data) {
      const report = (data && data.report) || null
      this.markResolved(id, { report, closedDeal: !!(report && report.closed_deal === true), confirmed: true })
      this.refresh({ activeId: id })
    },

    /**
     * Entrevista / Negociação / Parceria finalizada pelo StandardReportForm (`reported`). O contrato segue a regra da
     * central: só a Parceria fechada o oferece (a pergunta "Deseja realizar o report dos contratos…?" da sidebar) —
     * com outras pendências o aviso fica na confirmação e o "Cadastrar contrato" aparece na liberação.
     */
    onStandardFinalized(id, payload) {
      const data = payload || {}
      const reschedule = data.reschedule && data.reschedule.payload ? { ...data.reschedule } : null
      this.markResolved(id, {
        report: data.report || null,
        closedDeal: !!data.offersContract,
        confirmed: true,
        answers: data.answers || null,
        context: data.context || null,
        reschedule,
      })
      this.refresh({ activeId: id })
    },

    /**
     * "Deseja remarcar a reunião? Sim" que falhou depois do report salvo. O formulário compacto já saiu da tela (a
     * confirmação é da central), então a central guarda o corpo do `POST /api/appointments` e oferece o "Tentar
     * novamente" só do reagendamento — na confirmação e, se era a última pendência, na tela de liberação —, como a
     * página do Report faz. A trava deixa passar o reagendamento de um "não houve reunião" recém-gravado.
     */
    rescheduleFailed(id) {
      const resolution = this.resolutions[id]
      const reschedule = resolution && resolution.reschedule
      return !!(reschedule && reschedule.status !== 'done')
    },

    retryReschedule(id) {
      const resolution = this.resolutions[id]
      const reschedule = resolution && resolution.reschedule
      if (!reschedule || reschedule.status === 'saving' || reschedule.status === 'done') return Promise.resolve(false)
      const setReschedule = next => {
        if (this.$_destroyed || !this.resolutions[id]) return
        this.$set(this.resolutions[id], 'reschedule', { ...reschedule, ...next })
      }
      setReschedule({ status: 'saving' })

      return this.$store.dispatch(appointmentTypes.STORE_APPOINTMENT, reschedule.payload)
        .then(() => {
          setReschedule({ status: 'done', message: '' })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso', text: APP_COPY.appointmentCreated, icon: 'CoffeeIcon', variant: 'success',
            },
          })
          return true
        })
        .catch(error => {
          const message = rescheduleErrorMessage(error)
          setReschedule({ status: 'error', message })
          if (!isReportPendingError(error)) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!', text: message, icon: 'AlertTriangleIcon', variant: 'danger',
              },
            })
          }
          return false
        })
    },

    // Já tinha report (outra aba/dispositivo): resolvida, mostra o que foi gravado; sem oferta de contrato aqui.
    onAlreadyReported(id, report) {
      this.markResolved(id, { report: report || null, confirmed: true })
      this.refresh({ activeId: id })
    },

    /**
     * O servidor recusou o rascunho/finalize com a trava (423) e este compromisso não está mais entre as pendências:
     * foi cancelado, excluído ou transferido enquanto outras continuam. Avisa e recarrega a lista, que o tira de lá
     * (sem ele, o card ficaria "pendente" para sempre e a central nunca liberaria o acesso).
     */
    onPendingRejected(id) {
      this.$_withdrawnIds[id] = true
      this.announce = APP_COPY.notReportable
      this.$toast({
        component: ToastificationContent,
        props: { text: APP_COPY.notReportable, icon: 'AlertCircleIcon', variant: 'warning' },
      })
      this.refresh({ withdrawnId: id })
    },

    onDraftSaved(id) {
      const item = this.itemById(id)
      if (item && !item.hasDraft) item.hasDraft = true
    },

    // ---------------------------------------------------------------------------------------------------------
    // Confirmação e liberação
    // ---------------------------------------------------------------------------------------------------------
    savedCaption(item) {
      return [item.customerName, item.typeLabel, item.listWhen].filter(Boolean).join(' · ')
    },

    canRegisterContract(id) {
      const resolution = this.resolutions[id]
      return !!(resolution && resolution.closedDeal)
    },

    // O mesmo destino do antigo "Reportar agora" da sidebar (MX/Parceria com negócio fechado → cadastro do contrato).
    contractRoute(id) {
      return { name: 'contract-save', params: { blocked: false, appointment: id } }
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-hub {
  // Hover do card da lista (board v4-pendencias).
  --ar-hub-card-hover: #262f4b;

  position: relative;
  padding-bottom: 24px;
}

body:not(.dark-layout) .ar-hub {
  --ar-hub-card-hover: var(--ar-bg-field-hover);
}

.ar-hub__loading {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ar-text-muted);
}

// Troca bloqueado → liberado: crossfade de 240ms (o que sai fica por cima, fora do fluxo).
.ar-hub__stage {
  position: relative;
}

.ar-hub-swap-enter-active {
  animation: ar-fade-in var(--ar-dur-entrance) var(--ar-ease-standard) both;
}

.ar-hub-swap-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  animation: ar-fade-in var(--ar-dur-entrance) var(--ar-ease-standard) reverse both;
}

.ar-hub__blocked {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// =================================================================================================================
// Hero
// =================================================================================================================
.ar-hub__hero {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.ar-hub__hero-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-brand-tint);
  color: var(--ar-brand-text);
}

.ar-hub__hero-text {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 620px;
}

.ar-hub__title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -.2px;
  color: var(--ar-text-1);
}

.ar-hub__lead {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: var(--ar-text-2);
}

.ar-hub__help {
  margin: 12px 0 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-hub__help-icon {
  flex-shrink: 0;
  color: var(--ar-text-muted);
}

.ar-hub__progress-card {
  width: 280px;
  flex-shrink: 0;
  margin-left: auto;
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ar-hub__count {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.ar-hub__count-value {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-hub__count--sm .ar-hub__count-value {
  font-size: 16px;
  line-height: 24px;
}

.ar-hub__count-suffix {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

// Progresso segmentado (1 por pendência): enche em 300ms quando uma é resolvida.
.ar-hub__segments {
  display: grid;
  gap: 4px;
}

.ar-hub__segments--sm {
  width: 160px;
  flex-shrink: 0;
}

.ar-hub__seg {
  position: relative;
  height: 6px;
  overflow: hidden;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-divider);
}

.ar-hub__seg-fill {
  position: absolute;
  inset: 0;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-success);
  transform-origin: left center;
  transition: transform var(--ar-dur-progress) var(--ar-ease-standard);
}

// =================================================================================================================
// Erro de carregamento
// =================================================================================================================
.ar-hub__error {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.ar-hub__error-text {
  margin: 0;
}

// =================================================================================================================
// Split view
// =================================================================================================================
.ar-hub__split {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

// ---- Lista ----
.ar-hub__list {
  width: 400px;
  flex-shrink: 0;
  padding: 16px;
}

.ar-hub__list-title {
  margin: 0 0 12px;
  padding: 0 4px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-hub__items {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ar-hub__item {
  margin: 0;
  padding: 0;
}

// Placeholder estático (spec-v3 §8: nada em loop além do spinner).
.ar-hub__skeleton {
  height: 76px;
  border-radius: var(--ar-radius-card);
  border: 1px solid var(--ar-divider);
  background: var(--ar-bg-field);
}

.ar-hub__card {
  position: relative;
  z-index: 0;
  width: 100%;
  height: 76px;
  margin: 0;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-card);
  background: var(--ar-bg-field);
  color: var(--ar-text-1);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 200ms var(--ar-ease-standard),
    border-color 200ms var(--ar-ease-standard),
    box-shadow 200ms var(--ar-ease-standard);

  &:hover {
    background: var(--ar-hub-card-hover);
    border-color: var(--ar-control);
  }
}

.ar-hub__card--selected,
.ar-hub__card--selected:hover {
  background: var(--ar-selected-tint);
  border-color: var(--ar-brand-text);
  box-shadow: inset 0 0 0 .5px var(--ar-brand-text);
}

// Acabou de ser resolvida: o card tinge de verde por 600ms e volta.
.ar-hub__card-flash {
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: var(--ar-radius-card);
  background: var(--ar-success-flash);
  animation: ar-flash-out 600ms var(--ar-ease-standard) forwards;
}

.ar-hub__card-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ar-hub__card-line {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ar-hub__card-line + .ar-hub__card-line {
  gap: 8px;
}

.ar-hub__card-name {
  // Nunca some: com o chip de tipo e o "Rascunho iniciado" na mesma linha, quem encolhe depois disso é o rascunho.
  min-width: 56px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-hub__card-chip {
  padding: 0 8px;
}

// "Rascunho iniciado" encolhe com reticências (o tipo de compromisso fica inteiro).
.ar-hub__card-chip--draft {
  flex-shrink: 1;
  min-width: 0;
}

.ar-hub__card-chip-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ar-hub__card-date {
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-hub__card-status {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.ar-hub__card-chevron {
  color: var(--ar-text-2);
}

.ar-hub__list-caption {
  margin: 16px 0 0;
  padding: 0 4px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);

  > svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
}

// ---- Painel ----
// Sem `overflow: hidden`: a lista de motivos (popover do select) passa da borda do painel quando abre perto do
// rodapé. Os cantos de baixo do rodapé são arredondados nos próprios formulários (PendencyMomentXForm e
// StandardReportForm compacto).
.ar-hub__pane {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 480px;
  display: flex;
  flex-direction: column;
}

.ar-hub__pane-loading {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ar-text-muted);
}

.ar-hub__pane-inner {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ar-hub__pane-head {
  flex-shrink: 0;
  padding: 20px 24px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid var(--ar-divider);
}

.ar-hub__pane-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.ar-hub__pane-title-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ar-hub__pane-title {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-hub__pane-date {
  margin: 2px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-hub__pane-date-icon {
  flex-shrink: 0;
  color: var(--ar-text-muted);
}

.ar-hub__pane-position {
  height: 28px;
  flex-shrink: 0;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-pill);
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ar-text-2);
  white-space: nowrap;
}

.ar-hub__pane-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 4px 24px 16px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

// Confirmação: "Falta 1 pendência para liberar seu acesso." + "Próxima pendência".
.ar-hub__remaining {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

.ar-hub__remaining-icon {
  flex-shrink: 0;
  color: var(--ar-brand-text);
}

// Report salvo, reagendamento falhou: aviso + "Tentar novamente" (o mesmo do StandardReportForm na página).
.ar-hub__reschedule-error {
  width: 100%;
  align-items: center;
}

.ar-hub__reschedule-error-text {
  flex: 1 1 auto;
  min-width: 0;
}

.ar-hub__released-reschedule {
  margin: 0;
  padding: 0 0 10px;
}

.ar-hub__next,
.ar-hub__continue {
  min-width: 200px;
}

// =================================================================================================================
// Liberado
// =================================================================================================================
.ar-hub__released {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ar-hub__released-inner {
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ar-hub__released-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-success-tint-strong);
  color: var(--ar-success-text);
}

.ar-hub__released-lock {
  overflow: visible;
}

.ar-hub__released-title {
  margin-top: 20px;
}

.ar-hub__released-lead {
  margin-top: 8px;
}

.ar-hub__released-card {
  width: 100%;
  margin-top: 24px;
  padding: 16px 20px 8px;
  text-align: left;
}

.ar-hub__released-head {
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.ar-hub__released-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ar-hub__released-row {
  margin: 0;
  padding: 10px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  border-top: 1px solid var(--ar-divider);
}

.ar-hub__released-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-hub__released-end {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ar-hub__continue {
  margin-top: 24px;
}

.ar-hub__released-help {
  margin-top: 20px;
  justify-content: center;
}

// =================================================================================================================
// Telas menores (entre 992 e 1199px o menu fica sobreposto: a lista mantém os 400px do board)
// =================================================================================================================
@media (max-width: 991.98px) {
  .ar-hub__hero {
    flex-wrap: wrap;
  }

  .ar-hub__progress-card {
    width: 100%;
    margin-left: 0;
  }

  .ar-hub__split {
    flex-direction: column;
    align-items: stretch;
  }

  .ar-hub__list {
    width: 100%;
  }

  .ar-hub__pane {
    min-height: 0;
  }
}

@media (max-width: 575.98px) {
  .ar-hub__hero {
    gap: 16px;
  }

  .ar-hub__pane-head {
    padding: 16px;
    flex-wrap: wrap;
  }

  .ar-hub__pane-position {
    order: 3;
  }

  .ar-hub__pane-body {
    padding: 4px 16px 16px;
  }

  .ar-hub__released-row {
    flex-wrap: wrap;
  }

  .ar-hub__reschedule-error {
    flex-wrap: wrap;
  }

  .ar-hub__next,
  .ar-hub__continue {
    width: 100%;
    min-width: 0;
    height: 48px;
  }
}
</style>

<style lang="scss">
// Central aberta em tela sem o menu fixo: os botões fixos do App.vue somem (ver mounted).
@media (max-width: 1199.98px) {
  body.ar-hub-visible .float-bottom-left {
    display: none;
  }
}
</style>
